import { ExperiencePageFragment, HeroParagraph, Slices as SlicesParagraph } from "@/__generated__/graphql";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import IconLegend from "@components/IconLegend";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import { GlobalPageProps, slugify } from "@lib/utils";
import { GridContainer, GridWrapper } from "@theme/lib";
import { ReactElement } from "react";
import { useMobile } from "@/lib/hooks/useMobile";
import { WindowWidth } from "@/lib/hooks/WindowWidth";

export type ExperiencePageProps = ComponentProps<ExperiencePageFragment>;

export default function ExperiencePage({ previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, content, hero, informationSection, pageUrl }: ExperiencePageProps & GlobalPageProps): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero as HeroParagraph) : null;

  const is960 = useMobile(960);
  const isMobile = useMobile(WindowWidth.Mobile);

  const iconOrder = [
    'experience-status',
    'minimum-age',
    'starting-time',
    'experience-type',
    'group-size',
    'facilities',
    'accessibility',
  ];

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl} />
      }
      {heroProps && <Hero {...heroProps} />}
      <main>
        {informationSection && (
          <GridContainer>
            <GridWrapper alignment={'full-width'}>
              {informationSection.tiles && informationSection.tiles.length > 0 && (
                <IconLegend
                  toggle={isMobile}
                  toggleLabel="Experience details"
                  ariaLabel="Experience information"
                  grid={isMobile ? 1 : is960 ? 3 : 5}
                  items={[...informationSection.tiles]
                    .sort((a, b) => {
                      const aIndex = iconOrder.indexOf(slugify(a.label));
                      const bIndex = iconOrder.indexOf(slugify(b.label));
                      return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
                    })
                    .map(({ label, value }) => {
                      const slug = slugify(label);
                      const info = slug === 'experience-status' && value
                        ? `<div class="icon-status icon-status-${slugify(value)}"></div>${value}`
                        : value ?? undefined;
                      return {
                        type: '' as const,
                        label,
                        info,
                      };
                    })}
                />
              )}
            </GridWrapper>
          </GridContainer>
        )}
        {content && <Slices {...(content as SlicesParagraph[])} />}
      </main>
    </>
  );
}
